// Where the Workflows pages live: /workflows on the desktop, and the list
// hosted at /companion/workflows on a phone (the desktop shell sends every
// phone to the companion), so links stay in the shell they were followed
// from. The page has two tabs, Workflows and Activity (every run and job:
// ?tab=activity, its filters beside it); the old ?tab=runs and ?tab=jobs
// open Activity (Jobs only for the latter) and ?tab=data the Data sources
// page, /workflows/data. The run view and a job's page work at phone
// width; the editor, a new job and the data sources are desktop pages,
// and on a phone their links land on the list with a one-line notice
// (?desktop=editor|data: companion/paths.ts).

import { useLocation } from "react-router-dom";

export function useWorkflowsBase(): string {
  const { pathname } = useLocation();
  return pathname === "/companion" || pathname.startsWith("/companion/")
    ? "/companion/workflows"
    : "/workflows";
}

export const onPhone = (base: string) => base.startsWith("/companion");

/** The Activity tab (runs and jobs), optionally narrowed: `?tab=activity&kind=job&workflow=<slug>`. */
export const activityPath = (
  base: string,
  narrow: { kind?: "job"; workflow?: string } = {},
) => {
  const q = new URLSearchParams({ tab: "activity" });
  if (narrow.kind) q.set("kind", narrow.kind);
  if (narrow.workflow) q.set("workflow", narrow.workflow);
  return `${base}?${q}`;
};
/** A tab of the page: "runs" and "jobs" are the Activity tab (jobs narrowed), "data" the Data sources page. */
export const workflowsPath = (
  base: string,
  tab?: "activity" | "runs" | "data" | "jobs",
) =>
  tab === "data"
    ? dataSourcesPath(base)
    : tab === "jobs"
      ? jobsPath(base)
      : tab
        ? activityPath(base)
        : base;
export const workflowEditPath = (base: string, slug: string) =>
  `${base}/${encodeURIComponent(slug)}`;
/** A link that opens a workflow in the editor (a desktop page: on a phone, the list with its notice). */
export const workflowOpenPath = (base: string, slug: string, hash = "") =>
  onPhone(base) ? `${base}?desktop=editor` : `${workflowEditPath(base, slug)}${hash}`;
export const newWorkflowPath = (base: string, from?: string | null) =>
  `${base}/new${from ? `?from=${encodeURIComponent(from)}` : ""}`;
/** The run view (built by the run-view stream; this area only links to it), `node` selected (?node=<node id>: a stage, or a branch `<stage>[k]`). */
export const workflowRunPath = (
  base: string,
  runId: string,
  node?: string | null,
) =>
  `${base}/runs/${encodeURIComponent(runId)}${node ? `?node=${encodeURIComponent(node)}` : ""}`;
/**
 * The Data sources page (a desktop page: on a phone, the list with its
 * notice) and one source's page (desktop). Where Connections is on
 * (`connections`), there is one list: Settings › Connections, a source is
 * its connection's Explore (desktop), and on a phone its connection's panel.
 */
export const dataSourcesPath = (base: string, connections = false) =>
  connections ? `${onPhone(base) ? "/companion" : ""}/settings#connections` : onPhone(base) ? `${base}?desktop=data` : `${base}/data`;
export const dataSourcePath = (base: string, name: string, connections = false) =>
  connections
    ? onPhone(base)
      ? `/companion/settings?connection=${encodeURIComponent(name)}#connections`
      : connectionExplorePath(name)
    : onPhone(base)
      ? `${base}?desktop=data`
      : `${base}/data/${encodeURIComponent(name)}`;
/** A connection's Explore, Settings › Connections › name (its tables or endpoints, Query or Try a call): `ref` is its id or any of its names. Desktop. */
export const connectionExplorePath = (ref: string) => `/settings/connections/${encodeURIComponent(ref)}/explore`;
/** The Saved results page (named results steps publish for other runs) and one of them, desktop pages like the data sources. */
export const savedResultsPath = (base: string) =>
  onPhone(base) ? `${base}?desktop=data` : `${base}/saved-results`;
/** Published: every key of the workspace and what each can use from outside; a desktop page like the saved results (companionLocationFor is not changed: the app's port pins it). */
export const publishedPath = (base: string) =>
  onPhone(base) ? `${base}?desktop=data` : `${base}/published`;
/** Deployments (workflows as APIs; signed in, desktop pages: on a phone a link lands on the list with the editor notice, as companionLocationFor sends every /workflows/<x>): the list, one deployment (its page is its docs; `hash` an endpoint row, "#latest-pdfs") and the Deploy screen. */
export const deploymentsPath = (base: string) =>
  onPhone(base) ? `${base}?desktop=editor` : `${base}/deployments`;
export const deploymentPath = (base: string, name: string, hash = "") =>
  onPhone(base) ? `${base}?desktop=editor` : `${base}/deployments/${encodeURIComponent(name)}${hash}`;
/** What the Deploy screen opens with: a workflow (Deploy… on it), a deployment to change (Change…), a stored proposal (the bell, Go back), the version it goes back to, and a legacy deployment that stays as it is (its Deploy…). */
export interface DeployFrom {
  workflow?: string | null;
  to?: string | null;
  proposal?: string | null;
  back?: number | null;
  stays?: string | null;
}
export const deployPath = (base: string, from: DeployFrom = {}) => {
  if (onPhone(base)) return `${base}?desktop=editor`;
  const q = new URLSearchParams();
  for (const key of ["workflow", "to", "proposal", "back", "stays"] as const) {
    const value = from[key];
    if (value !== null && value !== undefined && value !== "") q.set(key, String(value));
  }
  const query = q.toString();
  return `${base}/deployments/new${query ? `?${query}` : ""}`;
};
/** Workflows → Recently deleted: the workflows deleted in the last 30 days, with Restore. */
export const recentlyDeletedPath = (base: string) => `${base}?view=trash`;
export const savedResultPath = (
  base: string,
  name: string,
  version?: number | null,
) =>
  onPhone(base)
    ? `${base}?desktop=data`
    : `${base}/saved-results/${encodeURIComponent(name)}${version ? `?v=${version}` : ""}`;
/** Activity narrowed to jobs, one job's page, and the brief form that starts one (desktop). */
export const jobsPath = (base: string) => activityPath(base, { kind: "job" });
export const jobPath = (base: string, runId: string) =>
  `${base}/jobs/${encodeURIComponent(runId)}`;
/** `from` = a job whose brief the form starts with ("Start again with changes"). */
export const newJobPath = (base: string, from?: string | null) =>
  `${base}/jobs/new${from ? `?from=${encodeURIComponent(from)}` : ""}`;
/** Settings → General, where the Workflow storage card is (disk used by runs and their sandboxes). */
export const WORKFLOW_STORAGE_PATH = "/settings#general";
